import { useCallback, useEffect, useRef, useState } from "react";
import { forecastArtifactService } from "../../utils/services/forecast-artifact.service";
import wsManager from "../../utils/websocketManager";

// Backstop for a missed/delayed WebSocket push (dropped connection, the
// connection not finishing its handshake before a fast server-side failure,
// etc.) — while generation is in flight, also re-check the stored status
// every few seconds so the UI can never get stuck showing "pending" forever
// after the server has actually already finished.
const POLL_INTERVAL_MS = 5000;

/**
 * Shared state for one product's "Product Artifact" report — used by both
 * ProductArtifactSections (the inline SKU table / issues / recommendations)
 * and ForecastArtifactButton (the full standalone report), so they always
 * agree on whether a generation is in flight, regardless of which one
 * (or the forecast pipeline itself) started it.
 *
 * On mount, fetches the latest stored artifact. If `autoGenerate` is true
 * and none has ever been generated for this product, starts one right away
 * — used on the "on landing" side (ProductArtifactSections), not by the
 * button, so only one place ever auto-triggers. Stays live afterwards via
 * the `forecastArtifactReady` WebSocket push, backed up by polling while
 * `pending` in case that push never arrives.
 *
 * Returns `{ artifact, generate }` — `artifact.status` is one of
 * "loading" | "none" | "pending" | "ready" | "error". `artifact.generated_at`
 * is set once `ready`; `artifact.startedAt` while `pending` (for the loader).
 */
export default function useForecastArtifact(
  productId,
  {
    autoGenerate = false,
    // "demand_forecast" on the admin Updated Forecast page (its own artifact,
    // from that engine's runs); undefined → the planning pages' artifact.
    engine,
    // Admins must say which client; a client user's token already does.
    clientId,
  } = {},
) {
  const [artifact, setArtifact] = useState({ status: "loading" });
  const autoStarted = useRef(false);
  const scope = { engine, clientId };

  const generate = useCallback(() => {
    if (!productId) return;
    setArtifact({ status: "pending", startedAt: new Date().toISOString() });
    forecastArtifactService.generate(productId, scope).catch((e) => {
      console.error("Failed to start the product artifact", e);
      setArtifact({
        status: "error",
        error: e?.response?.data?.message || "Couldn't start generation.",
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, engine, clientId]);

  useEffect(() => {
    autoStarted.current = false;
    if (!productId) {
      setArtifact({ status: "none" });
      return undefined;
    }
    let cancelled = false;
    setArtifact({ status: "loading" });
    forecastArtifactService
      .get(productId, scope)
      .then((doc) => {
        if (cancelled) return;
        const next = doc
          ? doc.status === "pending"
            ? { ...doc, startedAt: doc.updatedAt || null }
            : doc
          : { status: "none" };
        if (autoGenerate && next.status === "none" && !autoStarted.current) {
          autoStarted.current = true;
          generate();
          return;
        }
        setArtifact(next);
      })
      .catch((e) => {
        console.error("Failed to load the product artifact", e);
        if (!cancelled) setArtifact({ status: "none" });
      });
    return () => {
      cancelled = true;
    };
    // `generate` is stable per productId (see its own deps) — omitting it
    // here avoids re-running this fetch/auto-trigger effect on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, autoGenerate, engine, clientId]);

  useEffect(() => {
    if (!productId) return undefined;
    const unsub = wsManager.on("forecastArtifactReady", (payload) => {
      if (String(payload?.product) !== String(productId)) return;
      // The other engine's artifact for the same product isn't ours.
      if ((payload?.engine || "kaman") !== (engine || "kaman")) return;
      setArtifact({
        status: payload.status,
        data: payload.data,
        narrative: payload.narrative,
        error: payload.error,
        generated_at: payload.generated_at,
      });
    });
    return () => {
      if (typeof unsub === "function") unsub();
    };
  }, [productId, engine]);

  useEffect(() => {
    if (!productId || artifact.status !== "pending") return undefined;
    const timer = setInterval(() => {
      forecastArtifactService
        .get(productId, scope)
        .then((doc) => {
          if (doc && doc.status !== "pending") setArtifact(doc);
        })
        .catch(() => {});
    }, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, artifact.status, engine, clientId]);

  return { artifact, generate };
}
